import { useCallback, useEffect, useRef, useState } from 'react';
import { ArchivioPezzi, archivioIndexedDb, archivioInMemoria } from '../services/verbaliArchivioPezzi';
import {
  CorpoTermina, creaAudio, DatiAudioVerbale, elencaAudio, erroreRichiesta, nuovaRipresa, RiepilogoAudio, terminaAudio,
} from '../services/verbaliAudioService';
import { CodaInvio, creaCodaInvio, inviaAlServer } from '../services/verbaliInvioPezzi';
import {
  AmbienteRegistratore, ambienteRegistratoreBrowser, creaRegistratore, ErroreRegistrazione, LucchettoRegistrazione,
  lucchettoBrowser, MESSAGGIO_BLOCCHI_NON_INVIATI, nomeLucchetto, Registratore, scegliFormato, StatoLocaleVerbale,
  StatoRegistratore,
} from '../services/verbaliRegistratore';

// Registrazione della riunione dalla pagina del verbale: microfono, registratore a blocchi da
// 60 s, coda di invio con archivio locale (IndexedDB), Wake Lock, avviso all'uscita e ripresa
// dopo una scheda chiusa («Registrazione interrotta alle …» ⇒ Riprendi / Termina qui). Il
// consenso dei presenti (VerbaleConsenso) lo chiede la pagina PRIMA di `avvia()`.

export const VINCOLI_MICROFONO: MediaStreamConstraints = {
  audio: { echoCancellation: false, noiseSuppression: true, autoGainControl: true },
};

export const AVVISO_ARCHIVIO_IN_MEMORIA =
  'Il browser non permette di salvare la registrazione sul computer: tieni la pagina aperta e la connessione attiva';
export const AVVISO_SCHERMO =
  'Il browser non può impedire allo schermo di spegnersi: tieni il computer acceso e collegato alla corrente durante la riunione';
export const TESTO_ATTESA_RETE =
  'In attesa della rete: i blocchi sono salvati su questo computer. Se chiudi, riapri questa pagina per completare l\'invio';
export const TESTO_ALTRA_SCHEDA = 'Registrazione in corso in un\'altra scheda: continua da lì';
export const TESTO_ATTESA_RETE_MEMORIA = 'In attesa della rete: non chiudere la pagina finché i blocchi non sono stati inviati';

const ERRORE_PERMESSO_MICROFONO = 'Il browser non ha il permesso di usare il microfono: consentilo dalle impostazioni del sito e riprova';
const ERRORE_NESSUN_MICROFONO = 'Nessun microfono trovato: collega un microfono e riprova';
const ERRORE_MICROFONO_OCCUPATO = 'Il microfono è usato da un altro programma: chiudilo e riprova';
const ERRORE_NON_SUPPORTATO = 'Questo browser non permette di registrare: usa Chrome, Edge, Firefox o Safari aggiornati';
const ERRORE_MICROFONO = 'Non è stato possibile usare il microfono: riprova';
const ERRORE_INTERROTTA_IN_SOSPESO = 'C\'è una registrazione interrotta: riprendila o terminala prima di avviarne una nuova';
const ERRORE_ALTRA_SCHEDA = 'La registrazione di questo evento è aperta in un\'altra scheda: continua da lì o chiudila';
const ERRORE_AVVIO = 'Il browser non è riuscito ad avviare la registrazione: ricarica la pagina e riprova';

const CHIAVE_TOKEN = 'representative_token';
const AGGIORNAMENTO_MS = 200;
/** Dopo quanto un invio che non finisce (con la rete apparentemente presente) vale «in attesa della rete». */
const ATTESA_LENTA_MS = 10000;
/** Ogni quanto una scheda ferma ricontrolla se l'altra ha finito di registrare. */
export const INTERVALLO_ALTRA_SCHEDA_MS = 5000;

export interface SentinellaWakeLock {
  /** true dopo il rilascio (il browser lo rilascia quando la pagina non è visibile). */
  readonly released?: boolean;
  release(): Promise<void>;
  addEventListener?(tipo: 'release', cb: () => void): void;
}

export interface MisuratoreLivello {
  /** 0..1 */
  livello(): number;
  chiudi(): void;
}

export interface AmbienteRegistrazione {
  getUserMedia(vincoli: MediaStreamConstraints): Promise<MediaStream>;
  /** IndexedDB; null se non utilizzabile (si usa la memoria). */
  apriArchivio(): Promise<ArchivioPezzi | null>;
  creaCoda(opzioni: { archivio: ArchivioPezzi; eventId: string }): CodaInvio;
  registratore: AmbienteRegistratore;
  supporta(mime: string): boolean;
  /** MediaRecorder presente. */
  disponibile(): boolean;
  richiediWakeLock: (() => Promise<SentinellaWakeLock>) | null;
  creaMisuratore(stream: MediaStream): MisuratoreLivello | null;
  /** Una sola scheda per evento registra o chiude l'audio. */
  lucchetto: LucchettoRegistrazione;
  /** `navigator.storage.persist`: chiede di non sfrattare l'archivio quando il disco è pieno. */
  persistiArchivio: (() => Promise<boolean>) | null;
  intervalloAltraSchedaMs: number;
}

export interface RegistrazioneInterrotta {
  audioId: string;
  alle: Date;
  /** La registrazione era sul telefono (app): avviso dei minuti rimasti sul telefono. */
  dalTelefono?: boolean;
}

/** Livello del microfono da un AnalyserNode: RMS in dB, da -60 dB (0) a 0 dB (1). */
function misuratoreBrowser(stream: MediaStream): MisuratoreLivello | null {
  const w: any = typeof window !== 'undefined' ? window : undefined;
  const Contesto = w?.AudioContext || w?.webkitAudioContext;
  if (!Contesto) return null;
  const contesto: AudioContext = new Contesto();
  // Creato dopo attese (microfono, server): può nascere sospeso per le regole di riproduzione.
  contesto.resume?.().catch(() => undefined);
  const sorgente = contesto.createMediaStreamSource(stream);
  const analizzatore = contesto.createAnalyser();
  analizzatore.fftSize = 1024;
  sorgente.connect(analizzatore);
  const campioni = new Float32Array(analizzatore.fftSize);
  return {
    livello() {
      analizzatore.getFloatTimeDomainData(campioni);
      let somma = 0;
      for (let i = 0; i < campioni.length; i++) somma += campioni[i] * campioni[i];
      const rms = Math.sqrt(somma / campioni.length);
      if (!rms) return 0;
      return Math.max(0, Math.min(1, (20 * Math.log10(rms) + 60) / 60));
    },
    chiudi() {
      try { sorgente.disconnect(); } catch { /* già scollegata */ }
      contesto.close().catch(() => undefined);
    },
  };
}

function ambientePredefinito(): AmbienteRegistrazione {
  const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
  return {
    getUserMedia: vincoli => {
      if (!nav?.mediaDevices?.getUserMedia) {
        return Promise.reject(Object.assign(new Error(ERRORE_NON_SUPPORTATO), { name: 'NotSupportedError' }));
      }
      return nav.mediaDevices.getUserMedia(vincoli);
    },
    apriArchivio: () => archivioIndexedDb(),
    creaCoda: ({ archivio, eventId }) => creaCodaInvio({ archivio, invia: inviaAlServer, eventId }),
    registratore: ambienteRegistratoreBrowser(),
    supporta: mime => typeof MediaRecorder !== 'undefined'
      && typeof MediaRecorder.isTypeSupported === 'function'
      && MediaRecorder.isTypeSupported(mime),
    disponibile: () => typeof MediaRecorder !== 'undefined',
    richiediWakeLock: nav?.wakeLock && typeof nav.wakeLock.request === 'function'
      ? () => nav.wakeLock.request('screen')
      : null,
    creaMisuratore: misuratoreBrowser,
    lucchetto: lucchettoBrowser(),
    persistiArchivio: nav?.storage && typeof nav.storage.persist === 'function' ? () => nav.storage.persist() : null,
    intervalloAltraSchedaMs: INTERVALLO_ALTRA_SCHEDA_MS,
  };
}

function messaggioMicrofono(err: any): string {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return ERRORE_PERMESSO_MICROFONO;
    case 'NotFoundError':
    case 'OverconstrainedError':
      return ERRORE_NESSUN_MICROFONO;
    case 'NotReadableError':
    case 'AbortError':
      return ERRORE_MICROFONO_OCCUPATO;
    case 'NotSupportedError':
      return ERRORE_NON_SUPPORTATO;
    default:
      return ERRORE_MICROFONO;
  }
}

function messaggioErrore(err: any): string {
  if (err instanceof ErroreRegistrazione) return err.message;
  if (err?.isAxiosError || err?.response) return erroreRichiesta(err).messaggio;
  return (typeof err?.message === 'string' && err.message) || 'Errore imprevisto nella registrazione';
}

/** L'audio sul server non riceve più nulla (già chiuso o eliminato): lo stato locale non serve più. */
function audioNonPiuInRicezione(err: any): boolean {
  const d = erroreRichiesta(err);
  return d.codice === 'AUDIO_NON_IN_RICEZIONE' || d.status === 404;
}

const fermaTracce = (stream: MediaStream | null) => {
  try { stream?.getTracks().forEach(t => t.stop()); } catch { /* già fermo */ }
};

const attivo = (r: Registratore | null) => !!r && (r.stato === 'registra' || r.stato === 'pausa');

interface Contesto {
  archivio: ArchivioPezzi;
  coda: CodaInvio;
  persistente: boolean;
}

export function useRegistrazioneVerbale(eventId: string, ambienteDiProva?: Partial<AmbienteRegistrazione>) {
  const ambienteRef = useRef<AmbienteRegistrazione | null>(null);
  if (!ambienteRef.current) ambienteRef.current = { ...ambientePredefinito(), ...ambienteDiProva };

  const [stato, setStato] = useState<StatoRegistratore>('pronto');
  const [msRipresa, setMsRipresa] = useState(0);
  const [livello, setLivello] = useState(0);
  const [inAttesa, setInAttesa] = useState(0);
  const [interrotta, setInterrotta] = useState<RegistrazioneInterrotta | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [avvisi, setAvvisi] = useState<string[]>([]);
  const [soloInMemoria, setSoloInMemoria] = useState(false);
  const [terminando, setTerminando] = useState(false);
  const [lento, setLento] = useState(false);
  const [inLinea, setInLinea] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [archivioLocale, setArchivioLocale] = useState(true);
  const [audioId, setAudioId] = useState<string | null>(null);
  const [audioTerminato, setAudioTerminato] = useState<RiepilogoAudio | null>(null);
  const [altraScheda, setAltraScheda] = useState(false);

  const contestoRef = useRef<Promise<Contesto> | null>(null);
  /** Controllo di apertura (blocchi rimasti, stato locale): avvio e ripresa lo aspettano. */
  const controlloRef = useRef<Promise<void> | null>(null);
  const codaRef = useRef<CodaInvio | null>(null);
  const persistenteRef = useRef(true);
  const registratoreRef = useRef<Registratore | null>(null);
  const terminandoRef = useRef(false);
  const interrottaRef = useRef<RegistrazioneInterrotta | null>(null);
  const vivoRef = useRef(false);
  /** Rilascio del lucchetto dell'evento, se questa scheda lo tiene. */
  const rilascioRef = useRef<(() => void) | null>(null);
  /** Ultimo pezzo rifiutato in modo definitivo (la coda si è fermata su di lui). */
  const rifiutoRef = useRef<{ audioId: string } | null>(null);
  const persistitoRef = useRef(false);
  /** Rifà il controllo di apertura (quando l'altra scheda ha finito). */
  const ricontrollaRef = useRef<(() => void) | null>(null);
  const risorseRef = useRef<{ stream: MediaStream | null; misuratore: MisuratoreLivello | null; sentinella: SentinellaWakeLock | null }>(
    { stream: null, misuratore: null, sentinella: null });

  const impostaInterrotta = useCallback((v: RegistrazioneInterrotta | null) => {
    interrottaRef.current = v;
    setInterrotta(v);
  }, []);

  const aggiungiAvviso = useCallback((m: string) => {
    setAvvisi(a => (a.includes(m) ? a : [...a, m]));
  }, []);

  const impostaTerminando = useCallback((v: boolean) => {
    terminandoRef.current = v;
    setTerminando(v);
  }, []);

  const prendiLucchetto = useCallback(async (): Promise<boolean> => {
    if (rilascioRef.current) return true;
    let rilascio: (() => void) | null = null;
    try {
      rilascio = await ambienteRef.current!.lucchetto.prendi(nomeLucchetto(eventId));
    } catch {
      rilascio = () => undefined; // lucchetto guasto: si lascia lavorare
    }
    if (!rilascio) {
      if (vivoRef.current) {
        setAltraScheda(true);
        setErrore(ERRORE_ALTRA_SCHEDA);
      }
      return false;
    }
    if (!vivoRef.current) {
      rilascio();
      return false;
    }
    rilascioRef.current = rilascio;
    setAltraScheda(false);
    return true;
  }, [eventId]);

  const rilasciaLucchetto = useCallback(() => {
    const r = rilascioRef.current;
    rilascioRef.current = null;
    try { r?.(); } catch { /* già rilasciato */ }
  }, []);

  /** Pezzi di un audio ancora nell'archivio. */
  const pezziRimasti = useCallback(async (ctx: Contesto, id: string) => {
    try {
      return (await ctx.archivio.elenca(eventId)).filter(p => p.tipo === 'pezzo' && p.audioId === id).length;
    } catch {
      return 0;
    }
  }, [eventId]);

  const rilasciaRisorse = useCallback(() => {
    const r = risorseRef.current;
    fermaTracce(r.stream);
    try { r.misuratore?.chiudi(); } catch { /* già chiuso */ }
    r.sentinella?.release().catch(() => undefined);
    risorseRef.current = { stream: null, misuratore: null, sentinella: null };
    setLivello(0);
  }, []);

  const chiediWakeLock = useCallback(async () => {
    const richiedi = ambienteRef.current!.richiediWakeLock;
    if (!richiedi) {
      aggiungiAvviso(AVVISO_SCHERMO);
      return;
    }
    try {
      const sentinella = await richiedi();
      if (!attivo(registratoreRef.current)) {
        sentinella.release().catch(() => undefined);
        return;
      }
      const precedente = risorseRef.current.sentinella;
      if (precedente && precedente !== sentinella) precedente.release().catch(() => undefined);
      risorseRef.current.sentinella = sentinella;
      sentinella.addEventListener?.('release', () => {
        if (risorseRef.current.sentinella === sentinella) risorseRef.current.sentinella = null;
      });
    } catch {
      aggiungiAvviso(AVVISO_SCHERMO); // batteria scarsa, pagina non visibile: non è un errore
    }
  }, [aggiungiAvviso]);

  /** Invio dei rimasti e «Termina» sul server (chiusura ripresa dopo la riapertura o «Termina qui»). */
  const chiudiAudio = useCallback(async (ctx: Contesto, id: string, corpo: CorpoTermina, alle: Date) => {
    impostaTerminando(true);
    setErrore(null);
    if (!(await prendiLucchetto())) {
      if (vivoRef.current) impostaTerminando(false);
      return;
    }
    try {
      await ctx.coda.svuota();
      if (!vivoRef.current) return;
      // Blocchi di questo audio ancora da inviare: «Termina qui» li perderebbe. Si accettano
      // mancanti solo se la coda si è fermata su un blocco di questo audio rifiutato dal server.
      const rimasti = await pezziRimasti(ctx, id);
      const rifiutatoQui = ctx.coda.fermo() && rifiutoRef.current?.audioId === id;
      if (rimasti > 0 && !(corpo.accettaMancanti && rifiutatoQui)) {
        throw new ErroreRegistrazione('BLOCCHI_NON_INVIATI', MESSAGGIO_BLOCCHI_NON_INVIATI);
      }
      const { audio } = await terminaAudio(eventId, id, corpo);
      if (corpo.accettaMancanti && rimasti > 0) {
        await ctx.coda.scartaAudio(id).catch(() => undefined);
        if (vivoRef.current) {
          setErrore(null);
          aggiungiAvviso(rimasti === 1
            ? 'Un blocco rifiutato dal server è stato escluso dalla registrazione'
            : `${rimasti} blocchi rifiutati dal server sono stati esclusi dalla registrazione`);
        }
      }
      await ctx.archivio.salvaStato(eventId, null).catch(() => undefined);
      if (!vivoRef.current) return;
      impostaInterrotta(null);
      setAudioTerminato(audio || null);
      setMsRipresa(0);
    } catch (err) {
      if (!vivoRef.current) return;
      setErrore(messaggioErrore(err));
      if (audioNonPiuInRicezione(err)) {
        await ctx.coda.scartaAudio(id).catch(() => undefined);
        await ctx.archivio.salvaStato(eventId, null).catch(() => undefined);
        impostaInterrotta(null);
      } else {
        impostaInterrotta({ audioId: id, alle });
      }
    } finally {
      rilasciaLucchetto();
      if (vivoRef.current) impostaTerminando(false);
    }
  }, [eventId, aggiungiAvviso, impostaInterrotta, impostaTerminando, pezziRimasti, prendiLucchetto, rilasciaLucchetto]);

  // Archivio, coda e controllo di ciò che è rimasto dalla volta precedente.
  useEffect(() => {
    let vivo = true;
    vivoRef.current = true;
    const amb = ambienteRef.current!;
    const disiscrizioni: Array<() => void> = [];
    setStato('pronto');
    setMsRipresa(0);
    setInAttesa(0);
    setErrore(null);
    setAvvisi([]);
    setAudioId(null);
    setAudioTerminato(null);
    impostaInterrotta(null);

    const preparazione = (async (): Promise<Contesto> => {
      let archivio: ArchivioPezzi | null = null;
      try { archivio = await amb.apriArchivio(); } catch { archivio = null; }
      const persistente = !!archivio;
      if (!archivio) archivio = archivioInMemoria();
      const coda = amb.creaCoda({ archivio, eventId });
      if (!vivo) {
        coda.chiudi();
        return { archivio, coda, persistente };
      }
      persistenteRef.current = persistente;
      codaRef.current = coda;
      if (!persistente) {
        setArchivioLocale(false);
        aggiungiAvviso(AVVISO_ARCHIVIO_IN_MEMORIA);
      } else {
        setArchivioLocale(true);
      }
      disiscrizioni.push(coda.onCambio(n => {
        if (!vivo) return;
        setInAttesa(n);
        setSoloInMemoria(coda.soloInMemoria());
      }));
      disiscrizioni.push(coda.onAvviso(m => {
        if (!vivo) return;
        aggiungiAvviso(m);
        setSoloInMemoria(coda.soloInMemoria());
      }));
      disiscrizioni.push(coda.onErrore((m, p) => {
        if (!vivo) return;
        if (p.tipo === 'segnaposto') {
          aggiungiAvviso(`Un momento segnato non è stato salvato: ${m}`);
        } else {
          rifiutoRef.current = { audioId: p.audioId };
          setErrore(m);
        }
      }));
      return { archivio, coda, persistente };
    })();
    contestoRef.current = preparazione;

    const controlla = async () => {
      const ctx = await preparazione;
      if (!vivo) return;
      let locale: StatoLocaleVerbale | null = null;
      try { locale = (await ctx.archivio.leggiStato(eventId)) as StatoLocaleVerbale | null; } catch { locale = null; }
      let dati: DatiAudioVerbale | null = null;
      try { dati = await elencaAudio(eventId); } catch { dati = null; }
      if (!vivo) return;
      if (attivo(registratoreRef.current) || terminandoRef.current) return;

      // Un'altra scheda registra (o chiude) questo evento: archivio e stato locale sono i suoi.
      let altrove = false;
      try { altrove = !rilascioRef.current && await amb.lucchetto.occupato(nomeLucchetto(eventId)); } catch { altrove = false; }
      if (!vivo) return;
      if (altrove) {
        setAltraScheda(true);
        impostaInterrotta(null);
        return;
      }
      setAltraScheda(false);

      if (dati && Array.isArray(dati.audio)) {
        // Blocchi rimasti di un audio che non riceve più (chiuso, eliminato): il server li rifiuterebbe.
        const inRicezione = new Set(dati.audio
          .filter(a => a.origine === 'registrazione' && a.stato === 'in_registrazione' && !a.eliminatoAt)
          .map(a => String(a._id)));
        let rimasti: string[] = [];
        try { rimasti = (await ctx.archivio.elenca(eventId)).map(p => p.audioId); } catch { rimasti = []; }
        for (const id of Array.from(new Set(rimasti))) {
          if (!inRicezione.has(id)) await ctx.coda.scartaAudio(id).catch(() => undefined);
        }
        if (locale && !inRicezione.has(String(locale.audioId))) {
          await ctx.archivio.salvaStato(eventId, null).catch(() => undefined);
          locale = null;
        }
      }
      if (!vivo) return;

      // Blocchi rimasti nell'archivio: subito al server.
      ctx.coda.svuota().catch(() => undefined);
      if (attivo(registratoreRef.current) || terminandoRef.current) return;

      if (locale) {
        const alle = new Date(locale.ultimoPezzoAt || locale.iniziataAt || Date.now());
        setAudioId(locale.audioId);
        if (locale.terminaRichiesta) {
          // «Termina» era stato premuto: si completa la chiusura con il numero esatto di blocchi
          // della ripresa (un blocco perso si vede, 409, invece di sparire). Non attesa qui:
          // `terminando` diventa vero subito, così avvio e ripresa non partono.
          chiudiAudio(ctx, locale.audioId, { riprese: [{ indice: locale.ripresa, pezzi: locale.pezziCreati }] }, alle)
            .catch(() => undefined);
        } else {
          impostaInterrotta({ audioId: locale.audioId, alle });
        }
        return;
      }
      const candidata = (dati?.audio || [])
        .filter(a => a.origine === 'registrazione' && a.stato === 'in_registrazione' && a.interrottaAlle && !a.eliminatoAt)
        .sort((a, b) => (b.ordine || 0) - (a.ordine || 0))[0];
      if (candidata) {
        impostaInterrotta({
          audioId: String(candidata._id),
          alle: new Date(candidata.interrottaAlle as string),
          ...(candidata.dispositivo === 'app' ? { dalTelefono: true } : {}),
        });
      }
    };
    const avviaControllo = () => {
      const c = controlla().catch(() => undefined);
      controlloRef.current = c;
      return c;
    };
    ricontrollaRef.current = () => { avviaControllo(); };
    avviaControllo();

    return () => {
      vivo = false;
      vivoRef.current = false;
      const reg = registratoreRef.current;
      registratoreRef.current = null;
      reg?.interrompi(); // blocchi e stato restano nell'archivio: «interrotta» alla riapertura
      rilasciaRisorse();
      rilasciaLucchetto();
      ricontrollaRef.current = null;
      disiscrizioni.forEach(f => f());
      codaRef.current = null;
      contestoRef.current = null;
      controlloRef.current = null;
      preparazione.then(c => c.coda.chiudi()).catch(() => undefined);
    };
  }, [eventId, aggiungiAvviso, chiudiAudio, impostaInterrotta, rilasciaLucchetto, rilasciaRisorse]);

  /** Nuovo registratore sullo stream (ripresa 0 di un audio nuovo o ripresa successiva). */
  const avviaRegistratore = useCallback(async (ctx: Contesto, stream: MediaStream, id: string, ripresa: number, mimeType: string) => {
    const amb = ambienteRef.current!;
    const reg = creaRegistratore({ eventId, audioId: id, ripresa, archivio: ctx.archivio, coda: ctx.coda, ambiente: amb.registratore, mimeType });
    reg.onCambio(s => {
      if (registratoreRef.current !== reg) return;
      setStato(s);
      if (s === 'fermo') {
        rilasciaRisorse();
        setMsRipresa(reg.msRipresa());
        // Fermato dal browser (microfono scollegato, errore) senza «Termina».
        if (!terminandoRef.current) {
          registratoreRef.current = null;
          setStato('pronto');
          impostaInterrotta({ audioId: id, alle: new Date(reg.ultimoPezzoAt()) });
          rilasciaLucchetto();
        }
      }
    });
    registratoreRef.current = reg;
    risorseRef.current.stream = stream;
    try {
      reg.avvia(stream);
    } catch {
      registratoreRef.current = null;
      rilasciaRisorse();
      rilasciaLucchetto();
      setErrore(ERRORE_AVVIO);
      impostaInterrotta({ audioId: id, alle: new Date() });
      return;
    }
    if (!persistitoRef.current && persistenteRef.current && amb.persistiArchivio) {
      persistitoRef.current = true;
      amb.persistiArchivio().catch(() => false);
    }
    setAudioId(id);
    setAudioTerminato(null);
    impostaInterrotta(null);
    setMsRipresa(0);
    setStato(reg.stato);
    try { risorseRef.current.misuratore = amb.creaMisuratore(stream); } catch { risorseRef.current.misuratore = null; }
    await chiediWakeLock();
  }, [eventId, chiediWakeLock, impostaInterrotta, rilasciaLucchetto, rilasciaRisorse]);

  const microfono = useCallback(async (): Promise<MediaStream | null> => {
    const amb = ambienteRef.current!;
    if (!amb.disponibile()) {
      setErrore(ERRORE_NON_SUPPORTATO);
      return null;
    }
    try {
      return await amb.getUserMedia(VINCOLI_MICROFONO);
    } catch (err) {
      setErrore(messaggioMicrofono(err));
      return null;
    }
  }, []);

  const avvia = useCallback(async ({ avvisoConfermato = true }: { avvisoConfermato?: boolean } = {}) => {
    if (attivo(registratoreRef.current) || terminandoRef.current) return;
    const preparazione = contestoRef.current;
    if (!preparazione) return;
    setErrore(null);
    const ctx = await preparazione;
    await controlloRef.current;
    if (terminandoRef.current) return;
    if (interrottaRef.current) {
      // Lo stato locale è uno solo per evento: la registrazione interrotta va prima ripresa o chiusa.
      setErrore(ERRORE_INTERROTTA_IN_SOSPESO);
      return;
    }
    if (!(await prendiLucchetto())) return;
    const stream = await microfono();
    if (!stream) {
      rilasciaLucchetto();
      return;
    }
    if (!vivoRef.current || contestoRef.current !== preparazione) {
      fermaTracce(stream);
      rilasciaLucchetto();
      return;
    }
    const formato = scegliFormato(ambienteRef.current!.supporta);
    let id: string;
    try {
      const { audio } = await creaAudio(eventId, {
        origine: 'registrazione',
        avvisoConfermato,
        ...(formato.mimeType ? { mime: formato.mimeType } : {}),
      });
      id = String(audio._id);
    } catch (err) {
      fermaTracce(stream);
      rilasciaLucchetto();
      if (vivoRef.current) setErrore(messaggioErrore(err));
      return;
    }
    if (!vivoRef.current || contestoRef.current !== preparazione) {
      fermaTracce(stream);
      rilasciaLucchetto();
      return;
    }
    await avviaRegistratore(ctx, stream, id, 0, formato.mimeType);
  }, [eventId, avviaRegistratore, microfono, prendiLucchetto, rilasciaLucchetto]);

  const riprendiInterrotta = useCallback(async () => {
    const int = interrottaRef.current;
    const preparazione = contestoRef.current;
    if (!int || !preparazione || attivo(registratoreRef.current) || terminandoRef.current) return;
    setErrore(null);
    const ctx = await preparazione;
    await controlloRef.current;
    if (!interrottaRef.current || terminandoRef.current) return;
    if (!(await prendiLucchetto())) return;
    const stream = await microfono();
    if (!stream) {
      rilasciaLucchetto();
      return;
    }
    const formato = scegliFormato(ambienteRef.current!.supporta);
    let ripresa: number;
    try {
      ripresa = await nuovaRipresa(eventId, int.audioId, formato.mimeType || undefined);
    } catch (err) {
      fermaTracce(stream);
      rilasciaLucchetto();
      if (!vivoRef.current) return;
      setErrore(messaggioErrore(err));
      if (audioNonPiuInRicezione(err)) {
        await ctx.coda.scartaAudio(int.audioId).catch(() => undefined);
        await ctx.archivio.salvaStato(eventId, null).catch(() => undefined);
        impostaInterrotta(null);
      }
      return;
    }
    if (!vivoRef.current || contestoRef.current !== preparazione) {
      fermaTracce(stream);
      rilasciaLucchetto();
      return;
    }
    await avviaRegistratore(ctx, stream, int.audioId, ripresa, formato.mimeType);
  }, [eventId, avviaRegistratore, impostaInterrotta, microfono, prendiLucchetto, rilasciaLucchetto]);

  /** «Termina qui» (dopo la conferma della pagina): invia i rimasti e chiude accettando i mancanti. */
  const terminaInterrotta = useCallback(async () => {
    const int = interrottaRef.current;
    const preparazione = contestoRef.current;
    if (!int || !preparazione || attivo(registratoreRef.current) || terminandoRef.current) return;
    const ctx = await preparazione;
    await chiudiAudio(ctx, int.audioId, { accettaMancanti: true }, int.alle);
  }, [chiudiAudio]);

  const termina = useCallback(async () => {
    const reg = registratoreRef.current;
    if (!reg || reg.stato === 'pronto' || terminandoRef.current) return;
    impostaTerminando(true);
    setErrore(null);
    try {
      const audio = await reg.termina();
      if (registratoreRef.current !== reg) return; // pagina lasciata: il lucchetto l'ha già rilasciato la pulizia
      registratoreRef.current = null;
      rilasciaLucchetto();
      setStato('pronto');
      setMsRipresa(0);
      setAudioTerminato(audio);
    } catch (err) {
      if (registratoreRef.current !== reg) return;
      registratoreRef.current = null;
      rilasciaLucchetto();
      setStato('pronto');
      setErrore(messaggioErrore(err));
      // Come alla riapertura: l'ora dell'ultimo blocco.
      impostaInterrotta({ audioId: reg.audioId, alle: new Date(reg.ultimoPezzoAt()) });
    } finally {
      if (vivoRef.current) impostaTerminando(false);
    }
  }, [impostaInterrotta, impostaTerminando, rilasciaLucchetto]);

  const pausa = useCallback(() => {
    const reg = registratoreRef.current;
    if (!reg) return;
    reg.pausa();
    setMsRipresa(reg.msRipresa());
  }, []);

  const riprendi = useCallback(() => {
    const reg = registratoreRef.current;
    if (!reg) return;
    reg.riprendi();
  }, []);

  const segnaMomento = useCallback((etichetta: string) => {
    registratoreRef.current?.segnaMomento(etichetta);
  }, []);

  /** Rimanda subito i blocchi in attesa (es. dopo un nuovo accesso). */
  const riprovaInvio = useCallback(() => {
    codaRef.current?.svuota().catch(() => undefined);
  }, []);

  // Cronometro e livello del microfono.
  useEffect(() => {
    if (stato !== 'registra' && stato !== 'pausa') return undefined;
    const timer = setInterval(() => {
      const reg = registratoreRef.current;
      if (!reg) return;
      setMsRipresa(reg.msRipresa());
      let l = 0;
      if (reg.stato === 'registra') {
        try { l = risorseRef.current.misuratore?.livello() ?? 0; } catch { l = 0; }
      }
      setLivello(l);
    }, AGGIORNAMENTO_MS);
    return () => clearInterval(timer);
  }, [stato]);

  // Un invio che non finisce durante «Termina» vale «in attesa della rete» anche se il browser si crede in linea.
  useEffect(() => {
    if (!terminando) {
      setLento(false);
      return undefined;
    }
    const timer = setTimeout(() => setLento(true), ATTESA_LENTA_MS);
    return () => clearTimeout(timer);
  }, [terminando]);

  // Avviso all'uscita, rete, Wake Lock al ritorno visibile, nuovo accesso.
  useEffect(() => {
    const primaDiUscire = (e: BeforeUnloadEvent) => {
      const coda = codaRef.current;
      const registra = attivo(registratoreRef.current);
      // Con IndexedDB i blocchi in attesa sono al sicuro: si può uscire, l'invio riprende riaprendo la pagina.
      const aRischio = !!coda && (coda.soloInMemoria() || (!persistenteRef.current && coda.inAttesa() > 0));
      if (!registra && !aRischio) return;
      e.preventDefault();
      e.returnValue = '';
    };
    const alOnline = () => setInLinea(true); // la coda riparte da sola con `online`
    const alOffline = () => setInLinea(false);
    const alVisibilita = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      // Il browser rilascia il Wake Lock quando la pagina si nasconde: si richiede a ogni ritorno.
      const sentinella = risorseRef.current.sentinella;
      if (attivo(registratoreRef.current) && !(sentinella && sentinella.released === false)) chiediWakeLock();
      if (codaRef.current?.fermo()) codaRef.current.svuota().catch(() => undefined);
    };
    const alStorage = (e: StorageEvent) => {
      // repAxios non rinnova il token: dopo un nuovo accesso (anche da un'altra scheda) si riprova.
      if (e.key !== CHIAVE_TOKEN || !e.newValue) return;
      codaRef.current?.svuota().catch(() => undefined);
    };
    window.addEventListener('beforeunload', primaDiUscire);
    window.addEventListener('online', alOnline);
    window.addEventListener('offline', alOffline);
    window.addEventListener('storage', alStorage);
    document.addEventListener('visibilitychange', alVisibilita);
    return () => {
      window.removeEventListener('beforeunload', primaDiUscire);
      window.removeEventListener('online', alOnline);
      window.removeEventListener('offline', alOffline);
      window.removeEventListener('storage', alStorage);
      document.removeEventListener('visibilitychange', alVisibilita);
    };
  }, [chiediWakeLock]);

  // Registrazione aperta in un'altra scheda: si ricontrolla finché non ha finito (poi «interrotta» o niente).
  useEffect(() => {
    if (!altraScheda) return undefined;
    const amb = ambienteRef.current!;
    let inCorso = false;
    const timer = setInterval(async () => {
      if (inCorso || attivo(registratoreRef.current) || terminandoRef.current) return;
      inCorso = true;
      try {
        if (!(await amb.lucchetto.occupato(nomeLucchetto(eventId)))) {
          setAltraScheda(false);
          setErrore(e => (e === ERRORE_ALTRA_SCHEDA ? null : e));
          ricontrollaRef.current?.();
        }
      } catch { /* si riprova al prossimo giro */ } finally {
        inCorso = false;
      }
    }, amb.intervalloAltraSchedaMs);
    return () => clearInterval(timer);
  }, [altraScheda, eventId]);

  const attesaRete = terminando && inAttesa > 0 && (!inLinea || lento);

  return {
    stato,
    msRipresa,
    livello,
    inAttesa,
    interrotta,
    avvia,
    pausa,
    riprendi,
    segnaMomento,
    termina,
    riprendiInterrotta,
    terminaInterrotta,
    errore,
    /** Avvisi che non fermano nulla (archivio in memoria, schermo, momenti scartati, pezzi solo in memoria). */
    avvisi,
    /** Qualche blocco in attesa non è nell'archivio: chiudendo la pagina si perderebbe. */
    soloInMemoria,
    /** «Termina» o «Termina qui» in corso (invio dei blocchi e chiusura sul server). */
    terminando,
    /** Durante la chiusura i blocchi aspettano la rete: mostrare `testoAttesaRete`. */
    attesaRete,
    testoAttesaRete: archivioLocale ? TESTO_ATTESA_RETE : TESTO_ATTESA_RETE_MEMORIA,
    /** false = IndexedDB non disponibile, blocchi solo in memoria. */
    archivioLocale,
    audioId,
    /** Ultimo audio chiuso con successo (per rileggere l'elenco). */
    audioTerminato,
    riprovaInvio,
    /** Un'altra scheda registra questo evento: niente Riprendi/Termina qui, mostrare `TESTO_ALTRA_SCHEDA`. */
    altraScheda,
  };
}

export type RegistrazioneVerbale = ReturnType<typeof useRegistrazioneVerbale>;
